import { useMemo } from 'react'

import { useCustomSteps } from 'src/context/CustomStepsContext'
import { StepType, StepStatus } from 'src/graphql/types/steps'
import { formatSnakeValue, formatStepType } from 'src/lib/formatters'

import { FilterOption } from '../types'

interface UseStepsFilterProps {
  selectedSteps: string[]
  onStepsChange: (steps: string[]) => void
}

export const useStepsFilter = ({
  selectedSteps,
  onStepsChange,
}: UseStepsFilterProps) => {
  const customStepsCtx = useCustomSteps()
  const customSteps = customStepsCtx?.customSteps ?? []

  const baseOptions = useMemo<FilterOption[]>(() => {
    const statusOptions = () =>
      Object.values(StepStatus).map((status) => ({
        value: status,
        label: formatSnakeValue(status),
        selected: false,
      }))

    const builtInOptions = Object.values(StepType).map((type) => ({
      value: type,
      label: formatStepType(type),
      selected: false,
      options: statusOptions(),
    }))

    const customOptions = customSteps.map((customStep) => ({
      value: customStep.customStepId,
      label: customStep.label,
      selected: false,
      options: statusOptions(),
    }))

    return [...builtInOptions, ...customOptions]
  }, [customSteps])

  // Update options to reflect selected state
  const options = useMemo<FilterOption[]>(() => {
    return baseOptions.map((option) => {
      const isMainSelected = selectedSteps.some((selected) => {
        if (selected.includes(':')) {
          const [type] = selected.split(':')
          return type === option.value
        }
        return selected === option.value
      })

      const updatedSubOptions = option.options?.map((subOption) => {
        const isSubSelected = selectedSteps.some((selected) => {
          if (selected.includes(':')) {
            const [type, status] = selected.split(':')
            return type === option.value && status === subOption.value
          }
          return false
        })

        return {
          ...subOption,
          selected: isSubSelected,
        }
      })

      // Check if any substages are selected to determine if parent should be selected
      const hasSelectedSubOptions =
        updatedSubOptions?.some((subOption) => subOption.selected) || false

      return {
        ...option,
        selected: isMainSelected || hasSelectedSubOptions,
        options: updatedSubOptions,
      }
    })
  }, [baseOptions, selectedSteps])

  return {
    options,
    selected: selectedSteps,
    onChange: onStepsChange,
    isLoaded: true,
  }
}
